/**
 * Tâche 1482 (Eric) — Nettoyage des documents d'essai.
 *
 * > « Supprime d'un coup les 200 documents d'essai jamais traités qui
 * >   encombrent la relance groupée. »
 *
 * Eric a demandé l'aperçu ET la confirmation avant une suppression
 * définitive : la liste exacte s'affiche, chaque ligne est décochable, et le
 * bouton dit combien de documents partent. Un compteur seul ne permettrait
 * pas de repérer le document métier qui aurait un nom d'essai.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Broom, SpinnerGap, X } from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

export default function NettoyageEssaisPanel() {
  const { lang } = useLang();
  const L = (fr, en) => (lang === "fr" ? fr : en);
  const [apercu, setApercu] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [coches, setCoches] = useState([]);
  const [suppression, setSuppression] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/nettoyage-essais/apercu`, { withCredentials: true });
      setApercu(r.data);
      setCoches((r.data?.candidats || []).map((c) => c.id));
    } catch (_) {
      setApercu(null);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const basculer = (id) => {
    setCoches((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const supprimer = async () => {
    setSuppression(true);
    try {
      const r = await axios.post(
        `${API}/api/nettoyage-essais/supprimer`,
        { ids: coches },
        { withCredentials: true },
      );
      const { supprimes, refuses } = r.data;
      toast.success(L(
        `${supprimes} document(s) d'essai supprimé(s)${refuses > 0 ? ` · ${refuses} refusé(s)` : ""}`,
        `${supprimes} test document(s) deleted${refuses > 0 ? ` · ${refuses} refused` : ""}`));
      setOuvert(false);
      await charger();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : L("Suppression impossible", "Deletion failed"));
    } finally {
      setSuppression(false);
    }
  };

  if (!apercu || apercu.total === 0) return null;

  return (
    <div
      className="mb-6 rounded-xl border border-zinc-200 bg-zinc-50 px-5 py-4"
      data-testid="nettoyage-essais-panel"
    >
      <h3 className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
        <Broom size={16} weight="bold" />
        {L("Nettoyage des essais", "Test cleanup")}
      </h3>
      <p className="mt-1 text-xs text-zinc-600">
        {L(
          `${apercu.total} document(s) d'essai n'ont produit aucune connaissance et encombrent la relance groupée. Critère : ${apercu.criteres}.`,
          `${apercu.total} test document(s) produced no knowledge and clutter the bulk re-run. Criterion: ${apercu.criteres}.`
        )}
      </p>
      <Button
        size="sm"
        variant="outline"
        className="mt-3"
        onClick={() => setOuvert(true)}
        data-testid="nettoyage-ouvrir-btn"
      >
        {L(`Voir la liste et supprimer (${apercu.total})`, `Review and delete (${apercu.total})`)}
      </Button>

      {ouvert && (
        <div
          className="fixed inset-0 z-50 bg-zinc-900/60 flex items-center justify-center p-4"
          data-testid="nettoyage-modal"
        >
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-4">
              <div>
                <h4 className="font-semibold text-zinc-900">
                  {L("Documents d'essai à supprimer", "Test documents to delete")}
                </h4>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {L(
                    "La suppression est définitive et en cascade (connaissances, segments, fichiers). Décochez ce que vous voulez garder.",
                    "Deletion is permanent and cascading (knowledge, segments, files). Uncheck anything you want to keep."
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOuvert(false)}
                className="text-zinc-400 hover:text-zinc-700"
                data-testid="nettoyage-fermer"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-2 border-b border-zinc-100 flex items-center gap-3 text-xs">
              <button
                type="button"
                onClick={() => setCoches(apercu.candidats.map((c) => c.id))}
                className="text-[#002FA7] hover:underline font-semibold"
                data-testid="nettoyage-tout-cocher"
              >
                {L("Tout cocher", "Check all")}
              </button>
              <button
                type="button"
                onClick={() => setCoches([])}
                className="text-zinc-500 hover:underline"
                data-testid="nettoyage-tout-decocher"
              >
                {L("Tout décocher", "Uncheck all")}
              </button>
              <span className="ml-auto text-zinc-500" data-testid="nettoyage-compte-coches">
                {L(`${coches.length} coché(s)`, `${coches.length} checked`)}
              </span>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-zinc-100">
              {apercu.candidats.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-3 px-5 py-2 cursor-pointer hover:bg-zinc-50"
                  data-testid={`nettoyage-ligne-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={coches.includes(c.id)}
                    onChange={() => basculer(c.id)}
                    className="w-4 h-4 accent-[#002FA7]"
                    data-testid={`nettoyage-case-${c.id}`}
                  />
                  <span className="text-sm text-zinc-800 truncate">{c.title || "(sans titre)"}</span>
                  <span className="ml-auto text-[11px] text-zinc-400 whitespace-nowrap">
                    {(c.created_at || "").slice(0, 10)}
                  </span>
                </label>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-zinc-200 flex items-center justify-end gap-3">
              <Button variant="ghost" onClick={() => setOuvert(false)} data-testid="nettoyage-annuler">
                {L("Annuler", "Cancel")}
              </Button>
              <Button
                onClick={supprimer}
                disabled={coches.length === 0 || suppression}
                className="bg-rose-600 hover:bg-rose-500"
                data-testid="nettoyage-supprimer-btn"
              >
                {suppression && <SpinnerGap size={14} className="animate-spin mr-1" />}
                {L(
                  `Supprimer définitivement ${coches.length} document(s)`,
                  `Permanently delete ${coches.length} document(s)`
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
